import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { ArrowUpDown, LayoutList, Table as TableIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import PapersTable from "./PapersTable";
import PaperCard from "./PaperCard";
import CollapsibleLowScoringSection from "../../../components/CollapsibleLowScoringSection";
import highlightService from "../../../services/highlightService";
import {
  buildPaperEngagementEvent,
  enqueueResultEngagementEvent,
} from "../../../services/resultEngagementService";
import { extractPmidFromUrl, getPaperStableId } from "../../../shared/utils/paper";
import { usePaperContext } from "../../search/context/PaperContext";
import { splitPapersForDisplay } from "../../../utils/paperFilters";
import {
  DEFAULT_PAPER_SORT_MODE,
  PAPER_SORT_MODES,
  getPaperRelevanceScore,
  sortPapersForDisplay,
} from "../utils/paperSorting";

export default function ResultsDashboard({
  message,
  handleReferenceClick,
  setSelectedImage,
  currentSearchId,
  onRerunInPro,
  onOpenPaperAnalysis,
  sourceSelectionNudgeKey,
}) {
  const {
    contextPaperIds,
    contextPaperCount,
    maxContextPaperCount,
    toggleContextPaper,
  } = usePaperContext();
  const { t } = useTranslation();
  const [selectedPaperIds, setSelectedPaperIds] = useState(new Set());
  const [highlightedPaperId] = useState(null);
  const [highlightedPapers, setHighlightedPapers] = useState(new Set());
  const [viewMode, setViewMode] = useState("list"); // "list" | "table"
  const [sortMode, setSortMode] = useState(DEFAULT_PAPER_SORT_MODE);
  const impressionObserverRef = useRef(null);
  const impressionTargetsRef = useRef(new Map());
  const seenImpressionKeysRef = useRef(new Set());
  const showContextActions = true;
  const isContextSourceLimitReached = contextPaperCount >= maxContextPaperCount;

  const togglePaperSelection = (paperId) => {
    setSelectedPaperIds((prev) => {
      const next = new Set(prev);
      if (next.has(paperId)) {
        next.delete(paperId);
      } else {
        next.add(paperId);
      }
      return next;
    });
  };

  useEffect(() => {
    let isMounted = true;

    const loadHighlights = async () => {
      if (!currentSearchId) {
        if (isMounted) {
          setHighlightedPapers(new Set());
        }
        return;
      }

      const highlights = await highlightService.getSearchHighlights(currentSearchId);
      if (!isMounted) return;
      if (highlights && typeof highlights === "object") {
        const pmids = Object.keys(highlights).filter((key) => highlights[key] === true);
        setHighlightedPapers(new Set(pmids));
      } else {
        setHighlightedPapers(new Set());
      }
    };

    loadHighlights();
    return () => {
      isMounted = false;
    };
  }, [currentSearchId]);

  const papers = useMemo(
    () => sortPapersForDisplay(message.papers || [], sortMode),
    [message.papers, sortMode],
  );

  const trackPaperEvent = useCallback(
    (eventType, paper, index, surface, extra = {}) => {
      if (!currentSearchId) return;
      enqueueResultEngagementEvent(
        buildPaperEngagementEvent({
          eventType,
          paper,
          index,
          searchId: currentSearchId,
          query: message.query,
          searchMode: message.search_mode,
          surface,
          ...extra,
        }),
      );
    },
    [currentSearchId, message.query, message.search_mode],
  );
  const trackCsvExport = useCallback(
    ({ surface, exportScope, exportCount, highlightedOnly = false, metadata }) => {
      if (!currentSearchId) return;
      enqueueResultEngagementEvent({
        event_type: "csv_export",
        search_id: currentSearchId,
        query: message.query || undefined,
        search_mode: message.search_mode || undefined,
        surface,
        export_scope: exportScope,
        export_count: exportCount,
        highlighted_only: highlightedOnly,
        metadata,
      });
    },
    [currentSearchId, message.query, message.search_mode],
  );
  const getImpressionKey = useCallback(
    (paper, surface) => {
      const pmid = paper?.pmid || paper?.PMID || extractPmidFromUrl(paper?.url);
      return [
        currentSearchId || "no-search",
        surface,
        pmid || paper?.title || paper?.Title || "unknown-paper",
      ].join("|");
    },
    [currentSearchId],
  );
  const toggleContextPaperKeepingViewport = useCallback(
    (paper, event) => {
      const scrollContainer = document.querySelector("[data-search-scroll-container='true']");
      const paperCard = event?.currentTarget?.closest?.("[data-paper-card-id]");
      const beforeTop =
        scrollContainer && paperCard
          ? paperCard.getBoundingClientRect().top -
            scrollContainer.getBoundingClientRect().top
          : null;

      const sourcePaperId = getPaperStableId(paper);
      if (
        sourcePaperId &&
        isContextSourceLimitReached &&
        !contextPaperIds.has(sourcePaperId)
      ) {
        return;
      }
      toggleContextPaper(paper);
      if (sourcePaperId) {
        onOpenPaperAnalysis?.();
      }

      if (!scrollContainer || !paperCard || beforeTop === null) {
        return;
      }

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (!paperCard.isConnected) return;
          const afterTop =
            paperCard.getBoundingClientRect().top -
            scrollContainer.getBoundingClientRect().top;
          scrollContainer.scrollTop += afterTop - beforeTop;
        });
      });
    },
    [
      contextPaperIds,
      isContextSourceLimitReached,
      onOpenPaperAnalysis,
      toggleContextPaper,
    ],
  );
  const canAddContextPaper = useCallback(
    (paper) => {
      const paperId = getPaperStableId(paper);
      return Boolean(
        paperId &&
          (!isContextSourceLimitReached || contextPaperIds.has(paperId)),
      );
    },
    [contextPaperIds, isContextSourceLimitReached],
  );
  const isContextPaperSelected = useCallback(
    (paper) => {
      const paperId = getPaperStableId(paper);
      return Boolean(paperId && contextPaperIds.has(paperId));
    },
    [contextPaperIds],
  );

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const impressionTargets = impressionTargetsRef.current;

    const markSeen = (target) => {
      if (!target || seenImpressionKeysRef.current.has(target.key)) return;
      seenImpressionKeysRef.current.add(target.key);
      trackPaperEvent("paper_impression", target.paper, target.index, target.surface, {
        visibleDurationMs: Date.now() - target.visibleSince,
        maxVisibleRatio: target.maxVisibleRatio,
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = impressionTargets.get(entry.target);
          if (!target || seenImpressionKeysRef.current.has(target.key)) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            target.maxVisibleRatio = Math.max(target.maxVisibleRatio, entry.intersectionRatio);
            if (!target.visibleSince) {
              target.visibleSince = Date.now();
              target.timeoutId = window.setTimeout(() => {
                markSeen(target);
                observer.unobserve(entry.target);
              }, 1000);
            }
          } else {
            if (target.timeoutId) {
              window.clearTimeout(target.timeoutId);
            }
            target.timeoutId = null;
            target.visibleSince = null;
          }
        });
      },
      { threshold: [0, 0.5, 0.75, 1] },
    );

    impressionObserverRef.current = observer;
    impressionTargets.forEach((target, node) => {
      if (!seenImpressionKeysRef.current.has(target.key)) {
        observer.observe(node);
      }
    });
    return () => {
      impressionTargets.forEach((target) => {
        if (target.timeoutId) {
          window.clearTimeout(target.timeoutId);
        }
      });
      impressionTargets.clear();
      observer.disconnect();
      impressionObserverRef.current = null;
    };
  }, [trackPaperEvent]);

  useEffect(() => {
    seenImpressionKeysRef.current.clear();
  }, [currentSearchId]);

  const registerImpressionTarget = useCallback(
    (paper, index, surface) => (node) => {
      const observer = impressionObserverRef.current;
      if (!node) return;
      const key = getImpressionKey(paper, surface);
      if (seenImpressionKeysRef.current.has(key)) return;
      impressionTargetsRef.current.set(node, {
        key,
        paper,
        index,
        surface,
        visibleSince: null,
        maxVisibleRatio: 0,
        timeoutId: null,
      });
      if (observer) {
        observer.observe(node);
      }
    },
    [getImpressionKey],
  );
  const strongMatchCount = useMemo(
    () =>
      papers.reduce((count, paper) => {
        const rawScore = getPaperRelevanceScore(paper);
        const scorePercent =
          rawScore <= 1 ? rawScore * 100 : rawScore <= 10 ? rawScore * 10 : rawScore;
        return scorePercent > 50 ? count + 1 : count;
      }, 0),
    [papers],
  );
  const shouldShowRerunInPro =
    message.search_mode === "basic" &&
    typeof message.query === "string" &&
    message.query.trim().length > 0 &&
    strongMatchCount < 5;
  const { expandedPapers, collapsedPapers } = useMemo(
    () => splitPapersForDisplay(papers),
    [papers],
  );
  const primaryFirstPaper = expandedPapers[0] || collapsedPapers[0] || null;

  const handleToggleBookmark = async (paper, surface = "results") => {
    if (!currentSearchId) return;
    const pmid = paper.pmid || extractPmidFromUrl(paper.url);
    if (!pmid) return;

    try {
      const isHighlighted = await highlightService.toggleHighlight(
        currentSearchId,
        pmid,
        paper.title || ""
      );
      trackPaperEvent(
        isHighlighted ? "bookmark_add" : "bookmark_remove",
        paper,
        papers.findIndex((item) => item === paper),
        surface,
      );
      setHighlightedPapers((prev) => {
        const next = new Set(prev);
        if (isHighlighted) {
          next.add(pmid);
        } else {
          next.delete(pmid);
        }
        return next;
      });
    } catch (error) {
      const local = highlightService.getLocalHighlights([currentSearchId])[currentSearchId] || {};
      const pmids = Object.keys(local).filter((key) => local[key] === true);
      setHighlightedPapers(new Set(pmids));
    }
  };

  const renderPaperCard = (paper, idx) => {
    const paperId = getPaperStableId(paper, idx);
    const sourcePaperId = getPaperStableId(paper);
    const pmid = paper.pmid || extractPmidFromUrl(paper.url);
    const isBookmarked = pmid ? highlightedPapers.has(pmid) : false;
    return (
      <PaperCard
        key={paperId}
        index={idx}
        paper={paper}
        searchMode={message.search_mode}
        isContextSelected={showContextActions ? contextPaperIds.has(sourcePaperId) : false}
        onToggleContext={showContextActions ? toggleContextPaperKeepingViewport : () => {}}
        showContextButton={showContextActions}
        contextDisabled={
          showContextActions &&
          isContextSourceLimitReached &&
          !contextPaperIds.has(sourcePaperId)
        }
        sourceSelectionNudgeActive={
          Boolean(sourceSelectionNudgeKey) &&
          paper === primaryFirstPaper &&
          showContextActions &&
          !isContextPaperSelected(paper)
        }
        sourceSelectionNudgeKey={sourceSelectionNudgeKey}
        isBookmarked={isBookmarked}
        bookmarkDisabled={!pmid || !currentSearchId}
        onToggleBookmark={() => handleToggleBookmark(paper, "results_card")}
        impressionRef={registerImpressionTarget(paper, idx, "results_card")}
        onTitleClick={() => trackPaperEvent("paper_title_click", paper, idx, "results_card")}
        onFullTextClick={() => trackPaperEvent("paper_full_text_click", paper, idx, "results_card")}
        handleReferenceClick={handleReferenceClick}
        setSelectedImage={setSelectedImage}
        isFirstResult={paper === primaryFirstPaper}
      />
    );
  };

  return (
    <div data-results-dashboard="true" className="space-y-6">
      {shouldShowRerunInPro && (
        <div className="flex flex-col gap-3 rounded-2xl border border-blue-200 bg-gradient-to-r from-blue-50 via-white to-slate-50 p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">
              {t("app.searchModes.rerunInProTitle")}
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              {t("app.searchModes.rerunInProHint", { count: strongMatchCount })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onRerunInPro?.(message.query)}
            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
          >
            {t("app.searchModes.rerunInProCta")}
          </button>
        </div>
      )}

      {/* View Toggle Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
        <span className="text-sm font-semibold text-slate-700 px-2">
          {message.papers?.length || 0} {t("dashboard.results")}
        </span>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600">
            <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" />
            <span className="hidden sm:inline">{t("dashboard.sort.label")}</span>
            <select
              aria-label={t("dashboard.sort.label")}
              value={sortMode}
              onChange={(event) => setSortMode(event.target.value)}
              className="h-7 rounded-md border-0 bg-transparent pr-6 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={PAPER_SORT_MODES.MATCH_RATE}>
                {t("dashboard.sort.matchRate")}
              </option>
              <option value={PAPER_SORT_MODES.YEAR}>
                {t("dashboard.sort.year")}
              </option>
              <option value={PAPER_SORT_MODES.CITATIONS}>
                {t("dashboard.sort.citations")}
              </option>
            </select>
          </label>
          <div className="flex bg-slate-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode("list")}
              className={`p-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-all ${
                viewMode === "list"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <LayoutList className="w-4 h-4" />
              {t("dashboard.view.list")}
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={`p-1.5 rounded-md flex items-center gap-2 text-xs font-medium transition-all ${
                viewMode === "table"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <TableIcon className="w-4 h-4" />
              {t("dashboard.view.table")}
            </button>
          </div>
        </div>
      </div>

      {viewMode === "list" ? (
        // LIST VIEW LAYOUT
        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-4">
            {expandedPapers.map((paper) => {
               const originalIndex = papers.findIndex(p => p === paper);
               return renderPaperCard(paper, originalIndex !== -1 ? originalIndex : 0);
            })}
          </div>

          {collapsedPapers.length > 0 && (
            <CollapsibleLowScoringSection
              papers={collapsedPapers}
              renderPaper={(paper) => {
                 const originalIndex = papers.findIndex(p => p === paper);
                 return renderPaperCard(paper, originalIndex !== -1 ? originalIndex : 0);
              }}
            />
          )}
        </div>
      ) : (
        // TABLE VIEW LAYOUT
        <div>
          <PapersTable
            papers={papers}
            sortMode={sortMode}
            selectedPaperIds={selectedPaperIds}
            setSelectedPaperIds={setSelectedPaperIds}
            highlightedPaperId={highlightedPaperId}
            setSelectedImage={setSelectedImage}
            togglePaperSelection={togglePaperSelection}
            onToggleContextPaper={showContextActions ? toggleContextPaperKeepingViewport : null}
            isContextPaperSelected={showContextActions ? isContextPaperSelected : null}
            canAddContextPaper={showContextActions ? canAddContextPaper : null}
            highlightedPapers={highlightedPapers}
            onToggleBookmark={(paper) => handleToggleBookmark(paper, "results_table")}
            currentSearchId={currentSearchId}
            onTrackPaperEvent={trackPaperEvent}
            onTrackCsvExport={trackCsvExport}
            registerImpressionTarget={registerImpressionTarget}
            sourceSelectionNudgeKey={sourceSelectionNudgeKey}
            sourceSelectionNudgePaperId={
              primaryFirstPaper ? getPaperStableId(primaryFirstPaper) : null
            }
          />
        </div>
      )}
    </div>
  );
}
